import { useCallback } from "react";

import { type AudioGeneration } from "@prisma/client";
import { useSession } from "next-auth/react";

import { useAudioGenerationsContext } from "@musicfy/libs/AudioGenerationsProvider";
import modalAxios, { ModalFunction } from "@musicfy/libs/modalAxios";
import { api, downloadFile } from "@musicfy/utils";

import {
  type TAudioGeneration,
  type TBaseAudioGeneration,
} from "../IAudioGenerations";

export const useAudioGenerations = () => {
  const { audioGenerations, setAudioGenerations, isAudioGenerationsLoading } =
    useAudioGenerationsContext();
  const { data } = useSession();

  const createAudioGenerationsMutation =
    api.audioGenerations.createAudioGenerations.useMutation();
  const updateAudioGenerationMutation =
    api.audioGenerations.updateAudioGeneration.useMutation();
  const deleteAudioGenerationMutation =
    api.audioGenerations.deleteAudioGeneration.useMutation();
  const toggleSaveAudioGenerationMutation =
    api.audioGenerations.toggleSaveAudioGeneration.useMutation();
  const mintVideoTokenMutation =
    api.audioGenerations.mintVideoToken.useMutation();

  const createAudioGenerations = useCallback(
    async (
      baseAudioGenerations: TBaseAudioGeneration[]
    ): Promise<TAudioGeneration[]> => {
      const generationsToCreate: AudioGeneration[] = baseAudioGenerations.map(
        (ag) => {
          return {
            ...ag,
            userId: data?.user?.id || "",
            createdAt: new Date(),
            updatedAt: new Date(),
          };
        }
      );

      const createdAudioGenerations: TAudioGeneration[] =
        await createAudioGenerationsMutation.mutateAsync(generationsToCreate);

      const _newAudioGenerations = [
        ...createdAudioGenerations,
        ...audioGenerations,
      ];

      setAudioGenerations(_newAudioGenerations);

      return _newAudioGenerations;
    },
    [
      audioGenerations,
      createAudioGenerationsMutation,
      data?.user?.id,
      setAudioGenerations,
    ]
  );

  const updateAudioGeneration = useCallback(
    (audioGenerationId: string) => {
      const audioGenerationIndex = audioGenerations.findIndex(
        (audioGeneration) => audioGeneration.id === audioGenerationId
      );

      return async (
        audioGeneration: Partial<TAudioGeneration>,
        upload = false
      ) => {
        setAudioGenerations((prevAudioGenerations) => {
          const newAudioGenerations = [...prevAudioGenerations];

          const currentAudioGeneration =
            newAudioGenerations[audioGenerationIndex];

          if (!currentAudioGeneration) return newAudioGenerations;

          newAudioGenerations[audioGenerationIndex] = {
            ...currentAudioGeneration,
            ...audioGeneration,
          };

          return newAudioGenerations;
        });

        if (upload) {
          await updateAudioGenerationMutation.mutateAsync({
            id: audioGenerationId,
            ...audioGeneration,
          });
        }
      };
    },
    [audioGenerations, setAudioGenerations, updateAudioGenerationMutation]
  );

  const deleteAudioGeneration = useCallback(
    async (audioGenerationId: string) => {
      setAudioGenerations((prevAudioGenerations) => {
        return prevAudioGenerations.filter(
          (audioGeneration) => audioGeneration.id !== audioGenerationId
        );
      });

      await deleteAudioGenerationMutation.mutateAsync({
        id: audioGenerationId,
      });
    },
    [deleteAudioGenerationMutation, setAudioGenerations]
  );

  const downloadAudioGenerationVideo = useCallback(
    async (
      audioGeneration: AudioGeneration,
      imageFilename: string,
      type: string
    ) => {
      const modalToken = await mintVideoTokenMutation.mutateAsync({
        generationId: audioGeneration.id,
      });

      const response = await modalAxios(
        ModalFunction.GENERATE_VIDEO,
        modalToken
      ).post<string>("", {
        id: audioGeneration.id,
        user_id: data?.user?.id,
        type: type,
        audio_id: audioGeneration.audioId,
        audio_filename: audioGeneration.filename,
        image_filename: imageFilename,
        is_saved: audioGeneration.isSaved,
      });
      await downloadFile({ url: response.data });
    },
    [data?.user?.id, mintVideoTokenMutation]
  );

  const toggleSaveAudioGeneration = useCallback(
    async (audioGenerationId: string) => {
      const audioGeneration = audioGenerations.find(
        (audioGeneration) => audioGeneration.id === audioGenerationId
      );

      if (!audioGeneration) {
        return;
      }

      const updatedAudioGeneration =
        await toggleSaveAudioGenerationMutation.mutateAsync({
          id: audioGenerationId,
        });

      updateAudioGeneration(audioGenerationId)({
        isSaved: updatedAudioGeneration.isSaved,
        url: updatedAudioGeneration.url,
      });
    },
    [toggleSaveAudioGenerationMutation, audioGenerations, updateAudioGeneration]
  );

  return {
    audioGenerations,
    setAudioGenerations,
    isAudioGenerationsLoading,
    createAudioGenerations,
    updateAudioGeneration,
    deleteAudioGeneration,
    toggleSaveAudioGeneration,
    downloadAudioGenerationVideo,
  };
};
